import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme, type ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ChevronDownIcon, XIcon } from './Icons';

export interface TreeNode {
  value: number;
  label: string;
  icon?: string;
  children?: TreeNode[];
}

interface TreeSelectProps {
  name?: string;
  value?: number | string | null;
  onChange?: (value: number | undefined) => void;
  options: TreeNode[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  showClearButton?: boolean;
  error?: string;
  className?: string;
  /** Max height of the tree dropdown (default 320px) */
  dropdownMaxHeight?: number;
}

/** Find label for a value by traversing the tree */
function findLabel(nodes: TreeNode[], value: number): string | undefined {
  for (const node of nodes) {
    if (node.value === value) return node.label;
    if (node.children?.length) {
      const found = findLabel(node.children, value);
      if (found) return found;
    }
  }
  return undefined;
}

/** Closed folder icon (folder with no open lid) */
const FolderClosedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
  </svg>
);

/** Open folder icon */
const FolderOpenIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z" />
  </svg>
);

/** Collect all node values that have children (for full expand) */
function getAllExpandableValues(nodes: TreeNode[]): number[] {
  const result: number[] = [];
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      result.push(node.value);
      result.push(...getAllExpandableValues(node.children));
    }
  }
  return result;
}

interface TreeRowProps {
  node: TreeNode;
  depth: number;
  selectedValue: number | undefined | null;
  expanded: Set<number>;
  onToggleExpand: (value: number) => void;
  onSelect: (value: number) => void;
  colors: ThemeColors;
}

const TreeRow: React.FC<TreeRowProps> = ({
  node,
  depth,
  selectedValue,
  expanded,
  onToggleExpand,
  onSelect,
  colors,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = hasChildren && expanded.has(node.value);
  const isSelected = selectedValue !== undefined && selectedValue !== null && node.value === Number(selectedValue);

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'flex items-center gap-1.5 py-2 px-2 rounded-lg cursor-pointer transition-colors min-h-9',
          isSelected ? cn(colors.primary, colors.primaryText) : colors.interactiveHover
        )}
        style={{ paddingLeft: 8 + depth * 20 }}
      >
        <span className="w-6 flex-shrink-0 flex items-center justify-center">
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.value);
              }}
              className={cn('p-0.5 rounded -m-0.5', colors.textMuted)}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={cn('w-4 h-4 transition-transform', isExpanded && 'rotate-90')}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <span className="w-4" aria-hidden />
          )}
        </span>
        <span className="w-6 flex-shrink-0 flex items-center justify-center">
          {hasChildren ? (
            isExpanded ? (
              <FolderOpenIcon className={cn('w-5 h-5', colors.textMuted)} />
            ) : (
              <FolderClosedIcon className={cn('w-5 h-5', colors.textMuted)} />
            )
          ) : (
            <FolderClosedIcon className={cn('w-5 h-5', colors.textMuted)} />
          )}
        </span>
        <button
          type="button"
          className="flex-1 text-left flex items-center gap-2 min-w-0"
          onClick={() => onSelect(node.value)}
        >
          {node.icon && <span className="text-base flex-shrink-0 leading-none">{node.icon}</span>}
          <span className="truncate text-sm font-medium">{node.label}</span>
        </button>
      </div>
      {hasChildren && isExpanded && (
        <div className="border-l ml-3 pl-0" style={{ borderColor: 'var(--border)' }}>
          {node.children!.map((child) => (
            <TreeRow
              key={child.value}
              node={child}
              depth={depth + 1}
              selectedValue={selectedValue}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              colors={colors}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TreeSelect: React.FC<TreeSelectProps> = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  required = false,
  showClearButton = true,
  error,
  className,
  dropdownMaxHeight = 320,
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const numValue = value !== undefined && value !== null ? Number(value) : undefined;
  const selectedLabel = numValue !== undefined ? findLabel(options, numValue) : undefined;
  const hasValue = selectedLabel !== undefined;

  const updateDropdownRect = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 280) });
    }
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (isOpen) {
      updateDropdownRect();
      // Full expand: expand all nodes that have children
      const expandable = getAllExpandableValues(options);
      setExpanded(new Set(expandable));
      const onScrollOrResize = () => updateDropdownRect();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else {
      setDropdownRect(null);
    }
  }, [isOpen, updateDropdownRect, options]);

  const handleToggleExpand = useCallback((nodeValue: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeValue)) next.delete(nodeValue);
      else next.add(nodeValue);
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (nodeValue: number) => {
      if (disabled) return;
      onChange?.(nodeValue);
      setIsOpen(false);
    },
    [onChange, disabled]
  );

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled) return;
    onChange?.(undefined);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <input type="hidden" name={name} value={numValue ?? ''} readOnly required={required} />

      <div
        className={cn(
          'flex items-center w-full rounded-lg transition-all duration-200 border min-h-10',
          'px-2.5 py-2 text-sm',
          colors.surface,
          colors.border,
          isOpen && 'ring-2 shadow-sm',
          isOpen && colors.primaryBorder,
          !isOpen && !error && colors.interactiveHover,
          error && 'border-red-500',
          disabled && 'opacity-50 cursor-not-allowed',
          disabled && colors.surfaceHover,
          !disabled && 'cursor-pointer'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className={cn('flex items-center gap-1 flex-1 min-w-0', !hasValue && 'opacity-70')}>
          <ChevronDownIcon
            className={cn(
              'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200',
              isOpen && 'rotate-180',
              hasValue ? colors.primaryFg : colors.textMuted
            )}
          />
          <div className="flex-1 min-w-0 truncate">
            {hasValue ? (
              <span className={cn('font-medium text-sm truncate', colors.primaryFg)}>{selectedLabel}</span>
            ) : (
              <span className={cn('text-sm', colors.textMuted)}>{placeholder}</span>
            )}
          </div>
        </div>
        {showClearButton && hasValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'ml-0.5 p-1.5 rounded-md flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors',
              colors.textMuted,
              colors.interactiveHover
            )}
            title="Clear"
            tabIndex={-1}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {isOpen && !disabled && dropdownRect &&
        createPortal(
          <div
            ref={dropdownRef}
            className={cn(
              'fixed z-[9999] rounded-lg border shadow-lg overflow-hidden',
              colors.surface,
              colors.border
            )}
            style={{
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
              maxHeight: dropdownMaxHeight,
            }}
          >
            <div className="overflow-y-auto p-1.5" style={{ maxHeight: dropdownMaxHeight - 8 }}>
              {options.length === 0 ? (
                <div className={cn('py-4 px-3 text-sm', colors.textMuted)}>No options</div>
              ) : (
                options.map((node) => (
                  <TreeRow
                    key={node.value}
                    node={node}
                    depth={0}
                    selectedValue={numValue}
                    expanded={expanded}
                    onToggleExpand={handleToggleExpand}
                    onSelect={handleSelect}
                    colors={colors}
                  />
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TreeSelect;
